import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { MoreVertical } from "lucide-react";
import Receipt, { ReceiptExport } from "./receipt-component";
import {
  SalesReport,
} from "@/types/pos/sales-history";
import { useNavigate } from "react-router-dom";
import {
  SaleStatus,
  TransactionStatus,
} from "@/types/enums";
import { useErpTransactionMutation, useRetryTaxSubmissionMutation } from "@/services/transactionApi";
import { Transaction } from "@/types/pos/transaction";
import { toast } from "@/components/ui/use-toast";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

interface DataTableRowActionsProps {
  data?: Transaction | SalesReport;
  refetch: () => void;
  handleEdit?: (data: Transaction | SalesReport) => void;
  access: string;
}

export function DataTableRowActions({
  data,
  refetch,
  handleEdit,
  access,
}: DataTableRowActionsProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const erpAccess = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).hide_pos_erp
      );
    } catch {
      return "no_access";
    }
  })();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const receiptRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate()
  const [erpTransaction] = useErpTransactionMutation()

  const handlePrintInvoice = async () => {
    if (data) {
      // Assuming currentData.response.orders[0] has all the necessary data
      const invoiceData = data;
      const htmlContent = await ReceiptExport(invoiceData); // Get the HTML content

      // Create an invisible iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0px";
      iframe.style.height = "0px";
      iframe.style.border = "none";
      // Append the iframe to the body
      document.body.appendChild(iframe);

      // Write the HTML content to the iframe's document
      const iframeDoc =
        iframe.contentWindow?.document || iframe.contentDocument;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();

        // Wait until the iframe content is fully loaded
        iframe.onload = () => {
          // Trigger print
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          // Remove the iframe after printing
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        };
      }
    }
  };

  const [retryTaxSubmission] = useRetryTaxSubmissionMutation();

  const handleSrbTransaction = async () => {
    try {
      const resp = await retryTaxSubmission({ transaction_id: data?.id as number, tax_type: "srb", org_id: orgId });

      console.log("srb resp", { resp });
      if (resp?.data && (resp.data.status_code == 200 || resp.data.srb_invoice_id !== null)) {
        toast({
          variant: "success",
          title: resp.data.srb_invoice_id !== null ? "SRB Transaction Successfully" : resp.data.detail,
        });
      } else if (resp?.error) {
        throw resp.error;
      }
    } catch (error: any) {
      console.error("Error:", { error });
      toast({
        variant: "destructive",
        title: "Retry SRB failed",
        description: error?.data?.detail || "Please try again.",
      });
    }
  };

  return (
    <>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
            >
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-4">
            <DialogTrigger asChild>
              {data?.transaction_type !== SaleStatus.Refund && (
                <DropdownMenuItem
                  disabled={data?.status !== "Paid"}
                  onClick={() => navigate(`/admin/pos/sell/${data?.id}`)}
                  className="text-nowrap"
                >
                  <i className="fa-solid fa-cash-register mr-2"></i>
                  Refund
                </DropdownMenuItem>
              )}
            </DialogTrigger>

            {data?.status === TransactionStatus.Pending && (
              <DropdownMenuItem
                onClick={() => navigate(`/admin/pos/sell/${data?.id}`)}
                className="text-nowrap"
              >
                <i className="fa-solid fa-money-bill mr-2"></i>
                Pay Receipt
              </DropdownMenuItem>
            )}
            {!data?.submitted_to_authority && (
              <DropdownMenuItem
                onClick={handleSrbTransaction}
                className="text-nowrap"
              >
                <i className="fa-solid fa-arrow-rotate-right mr-2"></i>
                Retry SRB
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-nowrap w-full"
              onClick={handlePrintInvoice}
            >
              <i className="fa-solid fa-print mr-2"></i> Print Receipt
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Receipt Modal */}
        <DialogContent className="overflow-auto h-[350px]">
          {data ? (
            <div ref={receiptRef} className="printable-receipt">
              <Receipt salesReport={data as SalesReport} />
            </div>
          ) : (
            <p>No receipt data available.</p>
          )}
          <div className="w-full justify-center items-center gap-4 flex">
            <Button
              className="w-full gap-3"
              onClick={handlePrintInvoice}
              disabled={!data}
            >
              <i className="fa-solid fa-print"></i> Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
